import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'uz-Latn' | 'uz-Cyrl' | 'ru' | 'en';
export type ScannerMode = 'camera' | 'usb' | 'both';
export type Theme = 'system' | 'light' | 'dark';
export type ChimePreset = 'bell' | 'beep' | 'chord' | 'pop';
export type OrientationMode = 'any' | 'portrait' | 'landscape';
/**
 * `once` — chime once when the new order arrives (current default).
 * `persistent` — keep re-ringing every few seconds until the cashier accepts
 *   or cancels every pending order. Matches the "alarm" mode some partners
 *   asked for when the kitchen is loud and a single chime is missed.
 */
export type AlarmMode = 'once' | 'persistent';

interface UIState {
  language: Language;
  soundOn: boolean;
  chimeVolume: number; // 0..1
  chimePreset: ChimePreset;
  alarmMode: AlarmMode;
  /** Speak "Yangi buyurtma — {drinkName}" on new orders / persistent alarm ticks. */
  voiceAlertOn: boolean;
  scannerMode: ScannerMode;
  theme: Theme;
  orientation: OrientationMode;
  pushEnabled: boolean;
  tutorialDismissed: boolean;
  setLanguage: (lang: Language) => void;
  setSoundOn: (on: boolean) => void;
  setChimeVolume: (v: number) => void;
  setChimePreset: (p: ChimePreset) => void;
  setAlarmMode: (m: AlarmMode) => void;
  setVoiceAlertOn: (on: boolean) => void;
  setScannerMode: (mode: ScannerMode) => void;
  setTheme: (t: Theme) => void;
  setOrientation: (o: OrientationMode) => void;
  setPushEnabled: (on: boolean) => void;
  dismissTutorial: () => void;
}

const DEFAULTS = {
  language: 'uz-Latn' as Language,
  soundOn: true,
  chimeVolume: 0.7,
  chimePreset: 'bell' as ChimePreset,
  alarmMode: 'once' as AlarmMode,
  voiceAlertOn: false,
  scannerMode: 'both' as ScannerMode,
  theme: 'light' as Theme,
  orientation: 'any' as OrientationMode,
  pushEnabled: false,
  tutorialDismissed: false,
};

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setLanguage: (language) => set({ language }),
      setSoundOn: (soundOn) => set({ soundOn }),
      setChimeVolume: (chimeVolume) => set({ chimeVolume: Math.max(0, Math.min(1, chimeVolume)) }),
      setChimePreset: (chimePreset) => set({ chimePreset }),
      setAlarmMode: (alarmMode) => set({ alarmMode }),
      setVoiceAlertOn: (voiceAlertOn) => set({ voiceAlertOn }),
      setScannerMode: (scannerMode) => set({ scannerMode }),
      setTheme: (theme) => set({ theme }),
      setOrientation: (orientation) => set({ orientation }),
      setPushEnabled: (pushEnabled) => set({ pushEnabled }),
      dismissTutorial: () => set({ tutorialDismissed: true }),
    }),
    {
      name: 'hoopla-pos:ui',
      version: 5,
      // Preserve existing per-cashier preferences across version bumps.
      // Without this, every new persisted field forces every cashier back
      // to defaults — they re-set language, sound, theme, scanner, etc.
      migrate: (persisted) => {
        const prev = (persisted ?? {}) as Partial<UIState>;
        return { ...DEFAULTS, ...prev };
      },
    },
  ),
);
